import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, CircleCheck, Clock, FileText, Minus, Search, Trash2, Upload } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { usePode } from '@/stores/authStore';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import ImportarPlanilhaDialog from './ImportarPlanilhaDialog';
import {
  ETAPAS,
  ORDEM_ETAPAS,
  PASSOS,
  TOM_PONTO,
  TOM_SELO,
  dataCompleta,
  oQueFalta,
  quandoFoi,
  type Etapa,
  type ResumoFechamento,
  type Situacao,
} from './situacao';
import Preloader from '@/components/Preloader';

type Cotacao = {
  id: number;
  numero: number;
  descricao: string;
  data_cotacao: string | null;
  criada_em: string | null;
  status: string;
  origem: { id: number; numero: number } | null;
  fechamento: ResumoFechamento | null;
  itens: number;
  quantidade_total_m: number;
  situacao: Situacao;
};

const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const percentual = (v: number) => `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/** Sem acento e sem caixa, para "cotacao" achar "Cotação". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

export default function CotacoesListPage() {
  const [importando, setImportando] = useState(false);
  const [filtro, setFiltro] = useState<Etapa | 'todas'>('todas');
  const [busca, setBusca] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cotacoes'],
    queryFn: async () => (await api.get<{ data: Cotacao[] }>('/cotacoes')).data.data,
  });

  // A criada por último em cima. Pela última ação, abrir uma equalização antiga
  // já a punha no topo, na frente da cotação em que o comprador está trabalhando.
  const cotacoes = useMemo(
    () => [...(data ?? [])].sort((a, b) => (b.criada_em ?? '').localeCompare(a.criada_em ?? '') || b.id - a.id),
    [data],
  );

  const queryClient = useQueryClient();
  const podeExcluir = usePode('cotacao.excluir');
  const [excluindo, setExcluindo] = useState<Cotacao | null>(null);
  const excluir = useMutation({
    mutationFn: async (cotacao: Cotacao) => (await api.delete<{ message: string }>(`/cotacoes/${cotacao.id}`)).data,
    // Sai da lista na hora; a consulta se refaz em segundo plano, que com o banco no servidor demora.
    onSuccess: (_, cotacao) => {
      setExcluindo(null);
      queryClient.setQueryData<Cotacao[]>(['cotacoes'], (lista) => lista?.filter((c) => c.id !== cotacao.id));
      queryClient.removeQueries({ queryKey: ['cotacao', String(cotacao.id)] });
      void queryClient.invalidateQueries({ queryKey: ['cotacoes'] });
    },
  });
  const erroDaExclusao = excluir.error
    ? (() => {
        const dados = (excluir.error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data;
        return dados?.errors ? Object.values(dados.errors)[0][0] : (dados?.message ?? 'Não foi possível excluir a cotação.');
      })()
    : null;

  const contagem = useMemo(() => {
    const porEtapa = {} as Record<Etapa, number>;
    cotacoes.forEach((c) => (porEtapa[c.situacao.etapa] = (porEtapa[c.situacao.etapa] ?? 0) + 1));
    return porEtapa;
  }, [cotacoes]);

  const visiveis = cotacoes.filter((cotacao) => {
    if (filtro !== 'todas' && cotacao.situacao.etapa !== filtro) return false;
    if (!busca.trim()) return true;
    const alvo = normalizar(`${cotacao.descricao} nº ${cotacao.numero} #${cotacao.numero}`);
    return normalizar(busca.trim()).split(/\s+/).every((termo) => alvo.includes(termo));
  });

  if (isLoading) {
    return <Preloader telaCheia mensagem="Carregando cotações…" className="py-3xl" />;
  }

  if (isError) {
    return (
      <p className="px-lg py-lg text-[14px] text-danger">
        Não foi possível carregar as cotações. Recarregue a página para tentar de novo.
      </p>
    );
  }

  // O card da criada por último ganha destaque: é nela que o comprador está trabalhando.
  const maisRecente = cotacoes[0]?.id;

  return (
    <div className="px-lg py-lg">
      <div className="flex flex-wrap items-start justify-between gap-md mb-lg">
        <div>
          <h1 className="text-[18px] font-bold text-ink">Cotações</h1>
          <p className="text-[13px] text-muted">
            Onde cada cotação parou e qual é o próximo passo, da criada por último para a mais antiga.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setImportando(true)}
          className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white"
        >
          <Upload size={15} />
          Importar planilha do ERP
        </button>
      </div>

      <ImportarPlanilhaDialog aberto={importando} aoFechar={() => setImportando(false)} />

      {cotacoes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface px-lg py-lg text-center">
          <FileText className="mx-auto mb-sm text-muted" size={22} />
          <p className="text-[14px] font-bold text-ink">Nenhuma cotação ainda</p>
          <p className="text-[13px] text-muted">
            Importe a planilha do ERP para montar a primeira.
          </p>
          <button
            type="button"
            onClick={() => setImportando(true)}
            className="mt-md inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white"
          >
            <Upload size={15} />
            Escolher a planilha
          </button>
        </div>
      ) : (
        <>
          <div className="mb-md flex flex-wrap items-center justify-between gap-sm">
            <div className="flex flex-wrap gap-xs" role="group" aria-label="Filtrar por etapa">
              <Filtro ativo={filtro === 'todas'} onClick={() => setFiltro('todas')} rotulo="Todas" total={cotacoes.length} />
              {ORDEM_ETAPAS.filter((etapa) => contagem[etapa]).map((etapa) => (
                <Filtro
                  key={etapa}
                  ativo={filtro === etapa}
                  onClick={() => setFiltro(etapa)}
                  rotulo={ETAPAS[etapa].rotulo}
                  total={contagem[etapa]}
                  ponto={TOM_PONTO[ETAPAS[etapa].tom]}
                />
              ))}
            </div>

            <label className="relative block w-full sm:w-64">
              <span className="sr-only">Buscar cotação</span>
              <Search size={14} className="pointer-events-none absolute left-sm top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="search"
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Buscar por número ou nome"
                className="w-full rounded-md border border-border bg-surface py-xs pl-xl pr-sm text-[13px] text-ink placeholder:text-muted focus:border-accent focus:outline-none"
              />
            </label>
          </div>

          {visiveis.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border bg-surface px-lg py-lg text-center text-[13px] text-muted">
              Nenhuma cotação com esse filtro.
            </p>
          ) : (
            <ul className="grid gap-md md:grid-cols-2 xl:grid-cols-3">
              {visiveis.map((cotacao) => (
                <CartaoCotacao
                  key={cotacao.id}
                  cotacao={cotacao}
                  destaque={cotacao.id === maisRecente}
                  aoExcluir={
                    podeExcluir
                      ? () => {
                          excluir.reset();
                          setExcluindo(cotacao);
                        }
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}

      <Dialog open={excluindo !== null} onOpenChange={(aberto) => !excluir.isPending && !aberto && setExcluindo(null)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>Excluir a cotação Nº {excluindo?.numero}?</DialogTitle>
          <DialogDescription className="mt-xs">
            A {excluindo?.descricao} some com tudo o que tem: itens, rodadas, propostas e seus arquivos, equalização e
            fechamento. Não dá para desfazer.
            {excluindo?.origem && ` Os itens separados voltam para a Nº ${excluindo.origem.numero}.`}
          </DialogDescription>
          {erroDaExclusao && <p className="mt-sm text-[13px] font-bold text-danger">{erroDaExclusao}</p>}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setExcluindo(null)}
              disabled={excluir.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => excluindo && excluir.mutate(excluindo)}
              disabled={excluir.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-danger px-md py-sm text-[13px] font-bold text-white hover:bg-danger/90 disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden="true" />
              {excluir.isPending ? 'Excluindo…' : 'Excluir cotação'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Filtro({
  ativo,
  onClick,
  rotulo,
  total,
  ponto,
}: {
  ativo: boolean;
  onClick: () => void;
  rotulo: string;
  total: number;
  ponto?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={cn(
        'inline-flex items-center gap-xs rounded-full border px-sm py-xs text-[12px] font-bold transition-colors',
        ativo ? 'border-accent bg-accent/10 text-accent' : 'border-border bg-surface text-ink-soft hover:border-accent',
      )}
    >
      {ponto && <i className={cn('block size-2 rounded-full', ponto)} aria-hidden="true" />}
      {rotulo}
      <span className={cn('tabular-nums', ativo ? 'text-accent' : 'text-muted')}>{total}</span>
    </button>
  );
}

/** Os cinco passos, com o atual marcado. */
function Passos({ etapa, compacto = false }: { etapa: Etapa; compacto?: boolean }) {
  const { passo, tom } = ETAPAS[etapa];

  return (
    <ol className="flex items-center gap-[3px]" aria-label={`Passo ${passo + 1} de ${PASSOS.length}: ${PASSOS[passo]}`}>
      {PASSOS.map((nome, indice) => (
        <li
          key={nome}
          title={nome}
          className={cn(
            'h-[5px] rounded-full',
            compacto ? 'w-5' : 'w-8',
            indice < passo && 'bg-success',
            indice === passo && (etapa === 'fechada' ? 'bg-success' : TOM_PONTO[tom]),
            indice > passo && 'bg-border',
          )}
        />
      ))}
    </ol>
  );
}

function Selo({ etapa }: { etapa: Etapa }) {
  const { rotulo, tom } = ETAPAS[etapa];

  // Fechada é o fim do caminho: selo cheio, para não se confundir com o verde claro de "Pronta para negociar".
  if (etapa === 'fechada') {
    return (
      <span className="inline-flex items-center gap-xs rounded-full bg-success px-sm py-[2px] text-[12px] font-bold text-white">
        <CircleCheck size={13} strokeWidth={2.5} aria-hidden="true" />
        {rotulo}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-xs rounded-full px-sm py-[2px] text-[12px] font-bold', TOM_SELO[tom])}>
      <i className={cn('block size-[6px] rounded-full', TOM_PONTO[tom])} aria-hidden="true" />
      {rotulo}
    </span>
  );
}

function Fornecedores({ situacao }: { situacao: Situacao }) {
  return (
    <ul className="flex flex-wrap gap-xs">
      {situacao.fornecedores.map(({ apelido, situacao: estado }) => (
        <li
          key={apelido}
          title={
            estado === 'respondeu'
              ? `${apelido} mandou proposta nesta rodada`
              : estado === 'aguardando'
                ? `Nova proposta pedida à ${apelido}, sem resposta ainda`
                : situacao.rodada.numero > 1
                  ? `${apelido} mantém o preço da rodada anterior`
                  : `${apelido} ainda não mandou proposta`
          }
          className={cn(
            'inline-flex items-center gap-[3px] rounded border px-xs py-[1px] text-[11px] font-bold',
            estado === 'respondeu' && 'border-success/30 bg-success/10 text-success',
            estado === 'aguardando' && 'border-warning/30 bg-warning/10 text-warning',
            estado === 'sem_proposta' && 'border-border text-muted',
          )}
        >
          {estado === 'respondeu' && <Check size={11} strokeWidth={3} />}
          {estado === 'aguardando' && <Clock size={11} strokeWidth={2.5} />}
          {estado === 'sem_proposta' && <Minus size={11} strokeWidth={2.5} />}
          {apelido}
        </li>
      ))}
    </ul>
  );
}

function ProximoPasso({ cotacao, destaque = false }: { cotacao: Cotacao; destaque?: boolean }) {
  const { botao, destino } = ETAPAS[cotacao.situacao.etapa];

  return (
    <Link
      to={`/cotacoes/${cotacao.id}/${destino}`}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-xs whitespace-nowrap rounded-md px-md py-sm text-[13px] font-bold transition-colors',
        destaque
          ? 'bg-accent text-white hover:bg-accent/90'
          : 'border border-border bg-surface text-ink hover:border-accent hover:text-accent',
      )}
    >
      {botao}
      <ArrowRight size={14} />
    </Link>
  );
}

function CartaoCotacao({
  cotacao,
  destaque = false,
  aoExcluir,
}: {
  cotacao: Cotacao;
  destaque?: boolean;
  aoExcluir?: () => void;
}) {
  const { situacao } = cotacao;
  const fechada = situacao.etapa === 'fechada';

  return (
    <li
      className={cn(
        'flex min-w-0 flex-col rounded-lg border p-md transition-colors',
        destaque ? 'border-accent/60 bg-accent/5 ring-1 ring-accent/20' : 'border-border bg-surface hover:border-accent/40',
        // Faixa verde na lateral: a cotação terminou, dá para achar as fechadas de relance.
        fechada && 'border-l-4 border-l-success',
        fechada && !destaque && 'bg-success/5',
      )}
    >
      <div className="flex items-start justify-between gap-sm">
        <div className="min-w-0">
          {destaque && <p className="text-[11px] font-bold uppercase tracking-wide text-accent">Mais recente</p>}
          <Link
            to={`/cotacoes/${cotacao.id}`}
            className={cn(
              'block text-[24px] leading-tight font-bold tabular-nums hover:text-accent',
              destaque ? 'text-accent' : 'text-ink',
            )}
          >
            Nº {cotacao.numero}
          </Link>
        </div>
        <Selo etapa={situacao.etapa} />
      </div>

      <Link to={`/cotacoes/${cotacao.id}`} className="mt-xs block truncate text-[14px] font-bold text-ink hover:text-accent">
        {cotacao.descricao}
      </Link>
      <p className="text-[12px] text-muted tabular-nums">
        {cotacao.itens} linhas · {inteiro.format(cotacao.quantidade_total_m)} m
        {cotacao.origem && (
          <>
            {' '}· separada da{' '}
            <Link to={`/cotacoes/${cotacao.origem.id}`} className="font-bold text-accent hover:underline">
              Nº {cotacao.origem.numero}
            </Link>
          </>
        )}
      </p>

      <div className="mt-md border-t border-border pt-sm">
        <Passos etapa={situacao.etapa} />
        <p className="mt-xs line-clamp-2 text-[12px] text-muted">
          {cotacao.fechamento ? (
            <>
              <CircleCheck size={13} strokeWidth={2.5} className="mr-[3px] inline -mt-[2px] text-success" aria-hidden="true" />
              Fechada com a <b className="text-ink-soft">{cotacao.fechamento.fornecedor}</b> por{' '}
              <b className="tabular-nums text-ink-soft">{reais.format(cotacao.fechamento.valor_total)}</b>
              {cotacao.fechamento.ganho > 0 && (
                <>
                  {' '}· ganho de <b className="tabular-nums text-success">{reais.format(cotacao.fechamento.ganho)}</b> ({percentual(cotacao.fechamento.ganho_pct)})
                </>
              )}
            </>
          ) : (
            <>
              <span className="font-bold text-ink-soft">{situacao.rodada.nome}</span> · {oQueFalta(situacao)}
            </>
          )}
        </p>
      </div>

      <div className="mt-sm">
        <Fornecedores situacao={situacao} />
        <p className="mt-xs line-clamp-2 text-[12px] text-ink-soft">
          {situacao.ultima_acao.texto}{' '}
          <span className="text-muted" title={dataCompleta(situacao.ultima_acao.em)}>
            · {quandoFoi(situacao.ultima_acao.em)}
          </span>
        </p>
      </div>

      <div className="mt-auto flex items-center gap-xs pt-md">
        <div className="flex-1 [&>a]:w-full">
          <ProximoPasso cotacao={cotacao} destaque={destaque} />
        </div>
        {aoExcluir && (
          <button
            type="button"
            onClick={aoExcluir}
            title="Excluir cotação"
            aria-label={`Excluir a cotação Nº ${cotacao.numero} (${cotacao.descricao})`}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-transparent text-muted hover:border-danger/30 hover:bg-danger/10 hover:text-danger"
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}
